import {
  CalendarDate,
  CalendarDateTime,
  getDayOfWeek,
  getLocalTimeZone,
  parseAbsolute,
  parseDate,
  toCalendarDate,
  toZoned,
  today,
} from "@internationalized/date";
import { Array, Option } from "effect";

export const DAY_MINUTES = 24 * 60;

/** A span on one calendar day; times are minutes since midnight in the viewer's time zone. */
export interface TimeSlot {
  readonly day: CalendarDate;
  readonly startMinutes: number;
  readonly endMinutes: number;
}

export interface SchooljaarRange {
  readonly fromYear: number;
  readonly start: CalendarDate;
  readonly end: CalendarDate;
}

export const parseCalendarDate = (value: string | null | undefined): Option.Option<CalendarDate> =>
  value ? Option.liftThrowable(parseDate)(value) : Option.none();

export const getWeekStart = (date: CalendarDate): CalendarDate =>
  date.subtract({ days: getDayOfWeek(date, "nl-NL") });

export const getWeekDays = (weekStart: CalendarDate, count: number): CalendarDate[] =>
  Array.makeBy(count, (index) => weekStart.add({ days: index }));

export const isInWeek = (day: CalendarDate, weekStart: CalendarDate): boolean => {
  const offset = day.compare(weekStart);
  return offset >= 0 && offset < 7;
};

/** 1 = Monday … 7 = Sunday, as the backend stores weekdays. */
export const isoWeekday = (day: CalendarDate): number => getDayOfWeek(day, "nl-NL") + 1;

/** Gym is given Monday to Friday. */
export const isSchooldag = (day: CalendarDate): boolean => isoWeekday(day) <= 5;

/** The schooldag a day stands for: itself, or the Monday after a weekend. */
export const getLesdag = (day: CalendarDate): CalendarDate =>
  isSchooldag(day) ? day : day.add({ days: 8 - isoWeekday(day) });

/** The day the planning opens on: today, or at the weekend the Monday the docent plans for. */
export const getPlanningToday = (timeZone = getLocalTimeZone()): CalendarDate =>
  getLesdag(today(timeZone));

export const parseDateParam = (
  value: string | null | undefined,
  timeZone = getLocalTimeZone(),
): CalendarDate => Option.getOrElse(parseCalendarDate(value), () => getPlanningToday(timeZone));

/** The schooldag before or after a day, over the weekend. */
export const stepSchooldag = (day: CalendarDate, step: 1 | -1): CalendarDate => {
  let next = day.add({ days: step });
  while (!isSchooldag(next)) next = next.add({ days: step });
  return next;
};

export const getIsoWeekNumber = (date: CalendarDate): number => {
  const utc = Date.UTC(date.year, date.month - 1, date.day);
  const weekday = new Date(utc).getUTCDay() || 7;
  const thursday = new Date(utc + (4 - weekday) * 86_400_000);
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  return Math.floor((thursday.getTime() - yearStart) / 86_400_000 / 7) + 1;
};

/** Aug 1 – Jul 31 of the schooljaar in the URL (`2025-2026`), or of today's schooljaar. */
export const getSchooljaarRange = (
  schoolyear: string | undefined,
  todayDate = today(getLocalTimeZone()),
): SchooljaarRange => {
  const match = schoolyear ? /^(\d{4})-\d{4}$/.exec(schoolyear) : null;
  const currentFromYear = todayDate.month < 8 ? todayDate.year - 1 : todayDate.year;
  const fromYear = match ? Number(match[1]) : currentFromYear;
  return {
    fromYear,
    start: new CalendarDate(fromYear, 8, 1),
    end: new CalendarDate(fromYear + 1, 7, 31),
  };
};

export const clampToSchooljaar = (day: CalendarDate, range: SchooljaarRange): CalendarDate => {
  if (day.compare(range.start) < 0) return range.start;
  if (day.compare(range.end) > 0) return range.end;
  return day;
};

export const toAbsoluteIso = (
  day: CalendarDate,
  minutes: number,
  timeZone = getLocalTimeZone(),
): string => {
  const dateTime = new CalendarDateTime(
    day.year,
    day.month,
    day.day,
    Math.floor(minutes / 60),
    minutes % 60,
  );
  return toZoned(dateTime, timeZone).toAbsoluteString();
};

export const toTimeSlot = (
  startAt: string,
  duurMinuten: number,
  timeZone = getLocalTimeZone(),
): TimeSlot => {
  const zoned = parseAbsolute(startAt, timeZone);
  const startMinutes = zoned.hour * 60 + zoned.minute;
  return { day: toCalendarDate(zoned), startMinutes, endMinutes: startMinutes + duurMinuten };
};

export const minutesToLabel = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** How far `minutes` is into a block that starts at `start` and lasts `duur`; null outside it. */
export function getNuPositie(start: number, duur: number, minutes: number | null) {
  if (minutes === null || minutes < start || minutes >= start + duur) return null;
  return (minutes - start) / duur;
}

export const labelToMinutes = (label: string): Option.Option<number> => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(label.trim());
  if (!match) return Option.none();
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours > 23 || minutes > 59 ? Option.none() : Option.some(hours * 60 + minutes);
};
